import {
	Button,
	CardActions,
	CardContent,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import {
	useCreateSubFaqMutation,
	useUpdateSubFaqMutation,
} from '@/api/information/subFaqApiSlice';
import {
	CreateSubFaq,
	InformationqaQuestion,
} from '@/api/DTO/information/subFaq.interface';

interface Props {
	type: FormType;
	details?: InformationqaQuestion;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function SubFaqInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createSubFaq] = useCreateSubFaqMutation();
	const [updateSubFaq] = useUpdateSubFaqMutation();
	const { faqId } = useParams();

	const defaultValues: DefaultValues<CreateSubFaq> = {
		answerEn: details?.answerEn || '',
		answerZh: details?.answerZh || '',
		questionEn: details?.questionEn || '',
		questionZh: details?.questionZh || '',
		overviewId: faqId,
	};

	const formSchema = Yup.object().shape({
		answerZh: Yup.string().required(t('errorMessage.required')),
		questionZh: Yup.string().required(t('errorMessage.required')),
	});
	const methods = useForm<CreateSubFaq>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<CreateSubFaq> = async (data) => {
		const obj = { ...data };
		const response =
			type == FormType.create
				? await createSubFaq(obj)
				: await updateSubFaq({ ...obj, id: details?.id });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.createQuestionSuccess')
					: t('snackbar.updateQuestionSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate(`/information-manage/faq-list/sub-faq-list/${faqId}`);
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate(`/information-manage/faq-list/sub-faq-list/${faqId}`);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('subFaq.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('subFaq.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>
						{currentTab == 'zh' && (
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='questionZh'
										label={t('subFaq.question')}
										maxLength={100}
									/>
								</Grid>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='answerZh'
										multiline
										label={t('subFaq.answer')}
										maxLength={1000}
										minRows={4}
									/>
								</Grid>
							</Grid>
						)}
						{currentTab == 'en' && (
							<Grid container spacing={3} marginTop={0} marginBottom={3}>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='questionEn'
										label={t('subFaq.question')}
										maxLength={1000}
									/>
								</Grid>
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='answerEn'
										multiline
										label={t('subFaq.answer')}
										maxLength={5000}
										minRows={4}
									/>
								</Grid>
							</Grid>
						)}
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.create')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
